import { useEffect, useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import useReveal from "../components/useReveal";

const helpOptions = [
  { value: "", label: "Pick one..." },
  { value: "website", label: "Website / Shopify Store" },
  { value: "seo", label: "SEO & Organic Growth" },
  { value: "ads", label: "Meta Ads / Paid Growth" },
  { value: "ai", label: "AI & Automation" },
  { value: "everything", label: "A bit of everything" },
  { value: "other", label: "Something else" },
];

export default function Contact() {
  const [form, setForm] = useState({
    website: "",
    name: "",
    email: "",
    business: "",
    help: "",
    message: "",
    hp: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [errors, setErrors] = useState({});

  useReveal();

  useEffect(() => {
    document.title = "Contact — Sutranode";
  }, []);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please share your name.";
    if (!form.email.trim()) errs.email = "Please share an email so we can reply.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Hmm, that email doesn't look right.";
    if (!form.help) errs.help = "Pick what we can help with.";
    if (!form.message.trim()) errs.message = "Tell us a bit about what you have in mind.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.hp) return;

    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setStatus({ state: "sending", msg: "Sending your message..." });

    const helpLabel = helpOptions.find((o) => o.value === form.help)?.label || form.help;

    try {
      await addDoc(collection(db, "leads"), {
        name: form.name.trim(),
        email: form.email.trim(),
        business: form.business.trim() || null,
        website: form.website.trim() || null,
        help: form.help,
        helpLabel,
        message: form.message.trim(),
        source: "sutranode.in/contact",
        userAgent: navigator.userAgent,
        createdAt: serverTimestamp(),
        status: "new",
      });
    } catch (err) {
      console.error("Firestore save failed:", err);
      setStatus({
        state: "error",
        msg:
          "Couldn't save your message just now. No worries — message us directly on WhatsApp at +91 91081 81932 and we'll reply within a few hours.",
      });
      return;
    }

    const lines = [
      `Hi Sutranode — new enquiry from the website:`,
      ``,
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.business ? `Business: ${form.business}` : null,
      form.website ? `Website: ${form.website}` : null,
      `Looking for: ${helpLabel}`,
      ``,
      `Message:`,
      form.message,
    ].filter(Boolean);

    const waLink = `https://wa.me/919108181932?text=${encodeURIComponent(lines.join("\n"))}`;

    setStatus({
      state: "success",
      msg:
        "Got it — message saved. Opening WhatsApp so we can carry on the conversation. If it doesn't open automatically, message us at +91 91081 81932.",
    });

    setForm({ website: "", name: "", email: "", business: "", help: "", message: "", hp: "" });

    setTimeout(() => {
      window.open(waLink, "_blank");
    }, 600);
  };

  return (
    <section className="section contact-section page-with-nav-offset">
      <div className="container">
        <div className="contact-inner" data-reveal>
          <div className="s-eyebrow">Get in touch</div>
          <h2 className="contact-title">Tell us about <em>your project.</em></h2>
          <p className="contact-sub">
            If it feels like we could be a good fit, let's have a real conversation. No pressure, no
            sales theatre, just a useful discussion about what you're building and where it should go
            next.
          </p>

          <div className="contact-trust-grid" data-reveal>
            <div className="contact-trust-card">
              <strong>Reply time</strong>
              <span>Usually within a few hours.</span>
            </div>
            <div className="contact-trust-card">
              <strong>Best fit</strong>
              <span>Web, SEO, Meta ads, and AI systems.</span>
            </div>
            <div className="contact-trust-card">
              <strong>Need it faster?</strong>
              <span>WhatsApp is perfect for quick questions.</span>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit} noValidate data-reveal>
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="hp">Leave this field empty</label>
              <input id="hp" name="hp" tabIndex={-1} autoComplete="off" value={form.hp} onChange={update("hp")} />
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="website">Website</label>
                <input id="website" type="url" placeholder="https://yourbrand.com" value={form.website} onChange={update("website")} />
              </div>
              <div className="form-field">
                <label htmlFor="name">Your name</label>
                <input
                  id="name"
                  type="text"
                  required
                  placeholder="What should we call you?"
                  value={form.name}
                  onChange={update("name")}
                  aria-invalid={!!errors.name}
                />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  placeholder="you@yourbrand.com"
                  value={form.email}
                  onChange={update("email")}
                  aria-invalid={!!errors.email}
                />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>
              <div className="form-field">
                <label htmlFor="business">Business name</label>
                <input id="business" type="text" placeholder="Your business or project" value={form.business} onChange={update("business")} />
              </div>
            </div>

            <div className="form-field full">
              <label htmlFor="help">What can we help with?</label>
              <select id="help" required value={form.help} onChange={update("help")} aria-invalid={!!errors.help}>
                {helpOptions.map((o) => (
                  <option key={o.value} value={o.value} disabled={o.value === ""}>{o.label}</option>
                ))}
              </select>
              {errors.help && <span className="field-error">{errors.help}</span>}
            </div>

            <div className="form-field full">
              <label htmlFor="message">Tell us a bit about what you have in mind</label>
              <textarea
                id="message"
                required
                rows={5}
                placeholder="Goal, challenge, launch timing — a few lines is enough."
                value={form.message}
                onChange={update("message")}
                aria-invalid={!!errors.message}
              />
              <span className="field-help">A few lines on your goal, challenge, or launch timing is enough for us to reply well.</span>
              {errors.message && <span className="field-error">{errors.message}</span>}
            </div>

            <div className="submit-row">
              <button type="submit" className="btn-gold btn-submit" disabled={status.state === "sending"}>
                {status.state === "sending" ? "Sending..." : "Send message →"}
              </button>
              <p className="contact-privacy-note">
                By sending this, you're happy for us to reply by email or WhatsApp about your enquiry.
              </p>
            </div>

            {status.state !== "idle" && (
              <div
                className={`form-status ${
                  status.state === "success" ? "is-success" : status.state === "error" ? "is-error" : "is-sending"
                }`}
                role="status"
              >
                {status.msg}
              </div>
            )}
          </form>

          <div className="contact-alt" data-reveal>
            <span className="or-line">or message us directly</span>
            <a href="https://wa.me/919108181932" className="contact-wa" target="_blank" rel="noopener noreferrer">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/></svg>
              +91 9108181932
            </a>
            <div className="contact-meta">
              <span className="contact-live-dot"></span>
              <span>We usually reply within a few hours / Based in India</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
